import { useQueryClient } from '@tanstack/react-query';
import { useOwnedMutation } from './owned-mutation';
import { authenticatedCacheGeneration, onAuthenticatedSessionChange, requireAuthenticatedGeneration } from '@/shared/lib/authenticated-session';
import { captureModelCommandContext, requireModelCommandContext, type ModelCommandContext } from '@/shared/lib/model-command-context';
export { captureModelCommandContext, type ModelCommandContext } from '@/shared/lib/model-command-context';
import { toast } from 'sonner';
import type { ModuleResizePayload } from '@/shared/lib/module-resize-command';
import { api, type RebuildModuleBody } from './client';
import { ownModelCasBase, requestJson } from './request-json';
import { queryKeys } from './queries';
import { useViewerStore } from '@/shared/store/viewer-store';
import {
  MODULE_COMMAND_COMMITTED_EVENT,
  announceModuleCommandTrace,
  MODULE_STATE_COMMITTED_EVENT,
  MODULE_STATE_SAVE_SETTLED_EVENT,
  MODULE_STATE_SAVE_STARTED_EVENT,
  announceOwnModuleCommand,
  committedBundleVersion,
  moduleStateFingerprint,
  normalizeEditableModuleState,
} from '@/features/scene/module-state-sync';
import { bulkApplyReport, describeBulkApplyReport } from '@/features/material-editor/lib/bulkSkipReport';
import { announcePlacementEffect } from '@/features/scene/lib/placementEffectEvents';
import type { MaterialProfile } from '@/features/scene/types';
import type {
  HardwareMaterialSelection,
  ConnectorSystem,
  CatalogMaterialSwapCommand,
  ModuleFillItem,
  ModuleFillOperationPayload,
  ModuleFillState,
  ModuleAtelierIntent,
  ModuleAtelierResolution,
  ModuleInstanceLifecycleResult,
  ModuleStatePayload,
  ReplaceConnectorPlaneResult,
  UniversalBundle,
} from './types';
import { requireHardwareMaterialSelection } from '@/shared/lib/hardware-material-selection';

async function postJson<T>(url: string, body?: unknown): Promise<T> {
  return requestJson<T>(url, { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}) });
}


// Commands issued by one browser tab must be serialized before they reach the
// CAS boundary.  This is deliberately per-model (not global and not a retry):
// two tabs still conflict normally, while an add/state-save followed by a
// visible remove in the same tab observes the revision committed by its own
// preceding command instead of racing it.
const modelCommandTails = new Map<string, Promise<unknown>>();
const latestCommittedVersions = new Map<string, number>();
const committedStateFingerprints = new Map<string, Map<string, number>>();
const committedModuleStates = new Map<string, ModuleStatePayload>();
// Only a contiguous chain of our own CAS commits can advance a dirty base.
// A remote gap has no edge and still receives the normal 409.
const ownCommittedVersionEdges = new Map<string, Map<number, number>>();
onAuthenticatedSessionChange(() => {
  modelCommandTails.clear();
  latestCommittedVersions.clear();
  committedStateFingerprints.clear();
  committedModuleStates.clear();
  ownCommittedVersionEdges.clear();
  for (const handle of pendingGeometryRefetch.values()) clearTimeout(handle);
  pendingGeometryRefetch.clear();
});

// A few legacy command hooks announce their committed revision through the
// window event rather than returning it to this module. Feed that same-tab
// revision into the local command queue so a post-render state save cannot
// submit an old base after (for example) mirror/resize/add has committed.
if (typeof window !== 'undefined') {
  window.addEventListener(MODULE_COMMAND_COMMITTED_EVENT, (event: Event) => {
    const detail = (event as CustomEvent<{ modelId?: string; bundleVersion?: number; authGeneration?: number }>).detail;
    if (!detail?.modelId || detail.authGeneration !== authenticatedCacheGeneration()
      || !Number.isInteger(detail.bundleVersion)) return;
    const current = latestCommittedVersions.get(detail.modelId) ?? 0;
    latestCommittedVersions.set(detail.modelId, Math.max(current, Number(detail.bundleVersion)));
  });
}

function rememberCommittedVersion(modelId: string, payload: unknown): void {
  const version = committedBundleVersion(payload);
  if (version !== null) {
    const expected = ownModelCasBase(modelId, payload);
    if (expected !== undefined && version === expected + 1) {
      let edges = ownCommittedVersionEdges.get(modelId);
      if (!edges) { edges = new Map(); ownCommittedVersionEdges.set(modelId, edges); }
      edges.set(expected, version);
      while (edges.size > 32) edges.delete(edges.keys().next().value as number);
    }
    latestCommittedVersions.set(modelId, Math.max(latestCommittedVersions.get(modelId) ?? 0, version));
  }
}

/** Synchronous OCC head for this tab's already committed command chain. */
export function latestOwnCommittedVersion(modelId: string, fallback: number): number {
  return Math.max(fallback, latestCommittedVersions.get(modelId) ?? 0);
}

function rememberCommittedState(modelId: string, body: ModuleStateBody, payload: unknown): void {
  const version = committedBundleVersion(payload);
  if (version === null) return;
  const actual = payload as ModuleStatePayload;
  committedModuleStates.set(modelId, JSON.parse(JSON.stringify(actual)) as ModuleStatePayload);
  const fingerprint = moduleStateFingerprint({
    transforms: body.transforms ?? {},
    notes: body.notes ?? {},
  });
  let states = committedStateFingerprints.get(modelId);
  if (!states) {
    states = new Map();
    committedStateFingerprints.set(modelId, states);
  }
  if (fingerprint === moduleStateFingerprint(actual.state)) states.set(fingerprint, version);
  // Keep this local dedupe index bounded; it is only for the post-render
  // duplicate of the latest few own commands, never a retry/rebase store.
  while (states.size > 32) states.delete(states.keys().next().value as string);
}

function isSupersededOwnState(modelId: string, body: ModuleStateBody, expected: number | undefined): boolean {
  if (body.autosave !== true) return false;
  if (expected === undefined) return false;
  const latest = latestCommittedVersions.get(modelId);
  if (latest === undefined || latest <= expected) return false;
  const fingerprint = moduleStateFingerprint({
    transforms: body.transforms ?? {},
    notes: body.notes ?? {},
  });
  const committedFingerprintVersion = committedStateFingerprints.get(modelId)?.get(fingerprint);
  return committedFingerprintVersion !== undefined && committedFingerprintVersion <= expected;
}

function queuedExpectedVersion(
  modelId: string,
  cachedVersion: number | undefined,
  explicitVersion: number | undefined,
): number | undefined {
  if (explicitVersion !== undefined) {
    let version = explicitVersion;
    const edges = ownCommittedVersionEdges.get(modelId);
    while (edges?.has(version)) version = edges.get(version)!;
    return version;
  }
  const remembered = latestCommittedVersions.get(modelId);
  if (remembered === undefined) return cachedVersion;
  return Math.max(remembered, cachedVersion ?? 0);
}

function requiredCommandBaseline(
  modelId: string,
  cachedVersion: number | undefined,
): number {
  const expected = queuedExpectedVersion(modelId, cachedVersion, undefined);
  if (expected === undefined) throw new Error('Сначала дождитесь загрузки актуальной версии модели');
  return expected;
}

function enqueueModelCommand<T>(modelId: string, command: () => Promise<T>, owner?: ModelCommandContext): Promise<T> {
  const authGeneration = authenticatedCacheGeneration();
  const previous = modelCommandTails.get(modelId) ?? Promise.resolve();
  const run = previous.catch(() => undefined).then(async () => {
    requireAuthenticatedGeneration(authGeneration);
    if (owner) requireModelCommandContext(owner, modelId);
    const result = await command();
    requireAuthenticatedGeneration(authGeneration);
    return result;
  });
  // The tracked tail is intentionally fulfilled on command failure.  The
  // mutation caller still receives the original rejection, while the private
  // queue bookkeeping never creates an unhandled rejected promise.
  const tracked = run.then(() => undefined, () => undefined);
  void tracked.then(() => {
    if (modelCommandTails.get(modelId) === tracked) modelCommandTails.delete(modelId);
  });
  modelCommandTails.set(modelId, tracked);
  return run;
}

/**
 * Object-owned catalog material swap.  Kept in the common per-model command
 * queue so a material picked from an older render cannot overtake an edit
 * already committed by this browser tab.
 */
export function useCatalogMaterialSwap(modelId: string | null | undefined, kind: 'facade' | 'corpus') {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (body: CatalogMaterialSwapCommand) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.swapCatalogMaterial(modelId, kind, {
          ...body,
          expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
        });
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (result) => {
      if (modelId) void invalidateModel(qc, modelId, result);
    },
  });
}

/** Preview then CAS-commit one physical exchange.  A rejected/expired preview
 * is intentionally never retried with a reconstructed client command. */
export function useModulePositionExchange(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (ids: { sourceModuleId: string; targetModuleId: string }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        if (!cached?.bundleHash) throw new Error('Сначала дождитесь загрузки актуальной модели');
        const preview = await api.previewModulePositionExchange(modelId, {
          ...ids, expectedBundleVersion: requiredCommandBaseline(modelId, cached.bundleVersion), expectedBundleHash: cached.bundleHash,
        });
        const result = await api.applyModulePositionExchange(modelId, preview.token);
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (result) => {
      if (modelId) void invalidateModel(qc, modelId, result);
      /* ВОЛНА W: сам переезд обоих модулей показывает useModuleMoveAnimation
       * (дифф поз до/после перечитывания бандла); сюда остаётся только
       * bloom-вспышка «обмен состоялся». */
      announcePlacementEffect({ kind: 'burst' }, modelId);
    },
  });
}

/** Kept separate from commit so the Atelier can render the signed ghost and
 * let the operator cancel without creating a revision. */
export function usePreviewModulePositionExchange(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (ids: { sourceModuleId: string; targetModuleId: string }) => {
      if (!modelId) throw new Error('No active model');
      const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
      if (!cached?.bundleHash) throw new Error('Сначала дождитесь загрузки актуальной модели');
      return api.previewModulePositionExchange(modelId, { ...ids, expectedBundleVersion: requiredCommandBaseline(modelId, cached.bundleVersion), expectedBundleHash: cached.bundleHash });
    },
  });
}

export function useApplyModulePositionExchange(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (token: string) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const result = await api.applyModulePositionExchange(modelId, token);
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (result) => {
      if (modelId) void invalidateModel(qc, modelId, result);
      announcePlacementEffect({ kind: 'burst' }, modelId);
    },
  });
}

export type PanelPatchBody = {
  /** Immutable mt_materials identity. Display labels are never submitted. */
  materialId?: number;
  texture?: string;
  grain?: 'length' | 'width' | 'cross' | 'none' | '';
  materialProfile?: MaterialProfile;
  uvScale?: number;
  uvRotationDeg?: number;
  roughness?: number;
  metalness?: number;
  edgeBands?: Partial<
    Record<
      'L' | 'R' | 'T' | 'B',
      {
        material?: string | null;
        thicknessMm?: number | null;
        widthMm?: number | null;
        clear?: boolean;
      }
    >
  >;
};

type ModuleStateBody = {
  transforms?: Record<string, { position: [number, number, number]; scale: [number, number, number] }>;
  notes?: Record<string, string>;
  createSnapshot?: boolean;
  expectedBundleVersion?: number;
  /** Internal marker for the debounce path; never sent over the wire. */
  autosave?: boolean;
};

type SaveModuleStateOptions = {
  silent?: boolean;
  invalidate?: boolean;
};

type SavedModuleStatePayload = ModuleStatePayload & { stateReadbackOnly?: boolean };

function requireModuleStateAck(modelId: string, payload: ModuleStatePayload, expected?: number): void {
  const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
  const vector = (value: unknown) => Array.isArray(value) && value.length === 3
    && value.every((item) => typeof item === 'number' && Number.isFinite(item));
  const state = payload?.state;
  const valid = payload?.ok === true && payload.modelId === modelId
    && typeof payload.bundleVersion === 'number' && Number.isSafeInteger(payload.bundleVersion) && payload.bundleVersion >= 1
    && record(state) && state.schema === 'holzcom.module_edits.v1' && record(state.transforms) && record(state.notes)
    && Object.values(state.transforms).every((value) => record(value) && vector(value.position) && vector(value.scale))
    && Object.values(state.notes).every((value) => typeof value === 'string')
    && (expected === undefined || (payload.changed === true && payload.bundleVersion === expected + 1)
      || (payload.changed === false && payload.bundleVersion === expected));
  if (!valid) throw Object.assign(new Error('MODULE_STATE_ACK_INVALID'), { status: 502, code: 'MODULE_STATE_ACK_INVALID' });
}

function labelConnectorSystem(system: ConnectorSystem | string) {
  if (system === 'minifix') return 'Minifix';
  if (system === 'confirmat') return 'Confirmat';
  return 'Auto';
}

function invalidateModel(qc: ReturnType<typeof useQueryClient>, modelId: string, committed?: unknown) {
  if (committed) {
    rememberCommittedVersion(modelId, committed);
    announceOwnModuleCommand(modelId, committed);
  }
  const invalidations = [
    qc.invalidateQueries({ queryKey: queryKeys.hardwarePurchase(modelId) }),
    qc.invalidateQueries({ queryKey: queryKeys.models }),
    qc.invalidateQueries({ queryKey: queryKeys.bundle(modelId) }),
    qc.invalidateQueries({ queryKey: queryKeys.estimate(modelId) }),
  // Live-cost: the smeta badge and estimate flyout also read the canonical
  // resolve-or-create smeta. Without these keys the badge shows the price
  // as of the last mount, not as of the last edit.
    qc.invalidateQueries({ queryKey: queryKeys.viewerSmetaStatus(modelId) }),
    qc.invalidateQueries({ queryKey: queryKeys.viewerSmetaFlyout(modelId) }),
    qc.invalidateQueries({ queryKey: queryKeys.cutlist(modelId) }),
    qc.invalidateQueries({ queryKey: queryKeys.analysis(modelId) }),
    qc.invalidateQueries({ queryKey: queryKeys.validation(modelId) }),
    qc.invalidateQueries({ queryKey: queryKeys.productionReadiness(modelId) }),
    qc.invalidateQueries({ queryKey: queryKeys.history(modelId) }),
    qc.invalidateQueries({ queryKey: ['module-operations', modelId] }),
  // Module/bundle changes mean every cached graph is stale (rollups,
  // moduleEntities, productionDag, constraintGraph). Invalidate them so the
  // next Estimate / Structure flyout fetch picks up the rebuilt versions.
    qc.invalidateQueries({ queryKey: queryKeys.bundleGraphs(modelId) }),
    qc.invalidateQueries({ queryKey: queryKeys.moduleGraph(modelId) }),
    qc.invalidateQueries({ queryKey: queryKeys.autoDimensions(modelId) }),
  // A resize/mirror/swap/material change rewrites a module's interior, so any
  // open ModuleFillFlyout's plan is stale. Prefix-match invalidates every
  // cached ['module-fill', modelId, *] regardless of which module is open.
    qc.invalidateQueries({ queryKey: ['module-fill', modelId] }),
    qc.invalidateQueries({ queryKey: ['module-atelier', modelId] }),
    qc.invalidateQueries({ queryKey: queryKeys.ergonomics(modelId) }),
    qc.invalidateQueries({ queryKey: queryKeys.productionSpec(modelId) }),
  ];
  return Promise.all(invalidations);
}

async function _editingPost<T = unknown>(modelId: string, path: string, body?: unknown): Promise<T> {
  return api.post<T>(`/api/models/${encodeURIComponent(modelId)}/${path}`, body ?? {});
}

/**
 * The premium-layout endpoints are semantic edits.  Always attach the bundle
 * version that the tab rendered and serialize only same-tab commands; a 409
 * from another tab intentionally reaches the UI without an automatic retry.
 */
async function postVersionedEditingCommand<T extends { bundleVersion?: number }>(
  qc: ReturnType<typeof useQueryClient>,
  modelId: string,
  path: string,
  body: Record<string, unknown> = {},
): Promise<T> {
  return enqueueModelCommand(modelId, async () => {
    const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
    const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
    const result = await _editingPost<T>(modelId, path, {
      ...body,
      expectedBundleVersion: expected,
    });
    rememberCommittedVersion(modelId, result);
    return result;
  });
}

/** Gravity settle + stacking over the whole scene (settle_engine). */
export function useSettle(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async () => {
      if (!modelId) throw new Error('No active model');
      return postVersionedEditingCommand(qc, modelId, 'settle');
    },
    onSuccess: () => { if (modelId) invalidateModel(qc, modelId); },
  });
}

/** Multi-select align (align L/R/back/front/centre) — module_arrange. */
export function useArrangeAlign(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { moduleIds: string[]; edge: string }) => {
      if (!modelId) throw new Error('No active model');
      return postVersionedEditingCommand(qc, modelId, 'arrange/align', payload);
    },
    onSuccess: () => { if (modelId) invalidateModel(qc, modelId); },
  });
}

/** Multi-select distribute (equal gaps along x/z) — module_arrange. */
export function useArrangeDistribute(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { moduleIds: string[]; axis: string }) => {
      if (!modelId) throw new Error('No active model');
      return postVersionedEditingCommand(qc, modelId, 'arrange/distribute', payload);
    },
    onSuccess: () => { if (modelId) invalidateModel(qc, modelId); },
  });
}

/** Move a module edge to a world coordinate (driving_dimension.set_edge).
 * Used by the 2D plan editor to drag a module: set its left edge to newX and
 * its back edge to newZ. */
export function useDimensionEdge(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { moduleId: string; edge: string; coordinateMm: number }) => {
      if (!modelId) throw new Error('No active model');
      return postVersionedEditingCommand(qc, modelId, 'dimension/edge', payload);
    },
    onSuccess: () => {
      if (modelId) invalidateModel(qc, modelId);
    },
  });
}

/** One plan drag is one module-scope X/Z command and one bundle revision. */
export function usePlanModuleMove(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { moduleId: string; xMm: number; zMm: number }) => {
      if (!modelId) throw new Error('No active model');
      return postVersionedEditingCommand(qc, modelId, 'dimension/position', payload);
    },
    onSuccess: () => { if (modelId) invalidateModel(qc, modelId); },
  });
}

/** Move a visual furniture footprint's min corner to (x,z) in world mm. */
export function useFurnitureMove(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { entityId: string; xMm: number; zMm: number }) => {
      if (!modelId) throw new Error('No active model');
      return postVersionedEditingCommand(qc, modelId, 'furniture/move', payload);
    },
    onSuccess: () => { if (modelId) invalidateModel(qc, modelId); },
  });
}

/** Resize a visual furniture footprint to width×depth (mm), min corner fixed. */
export function useFurnitureResize(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { entityId: string; widthMm: number; depthMm: number }) => {
      if (!modelId) throw new Error('No active model');
      return postVersionedEditingCommand(qc, modelId, 'furniture/resize', payload);
    },
    onSuccess: () => { if (modelId) invalidateModel(qc, modelId); },
  });
}

/** Lock the current gap between two modules as a parametric constraint. */
export function useLockGap(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { a: string; b: string; axis?: string }) => {
      if (!modelId) throw new Error('No active model');
      return postVersionedEditingCommand(qc, modelId, 'constraints/lock-gap', payload);
    },
    onSuccess: () => {
      if (modelId) { invalidateModel(qc, modelId); qc.invalidateQueries({ queryKey: queryKeys.constraints(modelId) }); }
    },
  });
}

/** Re-solve all parametric constraints (constraint_engine.solve). */
export function useSolveConstraints(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async () => {
      if (!modelId) throw new Error('No active model');
      return postVersionedEditingCommand(qc, modelId, 'constraints/solve');
    },
    onSuccess: () => { if (modelId) invalidateModel(qc, modelId); },
  });
}

/** Preview constraint propagation on a cloned scene; never persists. */
export function useConstraintWhatIf(modelId: string | null | undefined) {
  return useOwnedMutation(modelId, {
    mutationFn: async (changes: Array<Record<string, unknown>>) => {
      if (!modelId) throw new Error('No active model');
      return api.constraintWhatIf(modelId, changes);
    },
    onError: (error) => toast.error('Предпросмотр не выполнен', { description: String(error) }),
  });
}

/** Multi-select pack-row (butt modules flush) — module_arrange.pack_row. */
export function useArrangePackRow(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { moduleIds: string[]; axis: string; gapMm?: number }) => {
      if (!modelId) throw new Error('No active model');
      return postVersionedEditingCommand(qc, modelId, 'arrange/pack-row', payload);
    },
    onSuccess: () => { if (modelId) invalidateModel(qc, modelId); },
  });
}

type LayoutCommandResult = {
  ok: boolean;
  bundleVersion?: number;
  error?: string;
  copyCount?: number;
  moves?: Array<{ moduleId: string }>;
  painted?: Array<{ moduleId: string; facadesPainted: number }>;
  unplaced?: string[];
};

function useLayoutMutation(
  modelId: string | null | undefined,
  path: string,
  success: (result: LayoutCommandResult) => string,
) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: unknown) => {
      if (!modelId) throw new Error('No active model');
      const result = await postVersionedEditingCommand<LayoutCommandResult>(qc, modelId, path, payload as Record<string, unknown>);
      if (!result.ok) throw new Error(result.error || 'Команда не выполнена');
      return result;
    },
    onSuccess: (result) => {
      if (modelId) invalidateModel(qc, modelId);
      toast.success(success(result));
    },
    onError: (error) => toast.error('Расстановка не выполнена', { description: String(error) }),
  });
}

export function useArrangeArray(modelId: string | null | undefined) {
  return useLayoutMutation(modelId, 'arrange/array', (result) => `Создано копий: ${result.copyCount ?? 0}`);
}

export function useDimensionGap(modelId: string | null | undefined) {
  return useLayoutMutation(modelId, 'dimension/gap', () => 'Зазор применён');
}

export function useMaterialBrush(modelId: string | null | undefined) {
  return useLayoutMutation(modelId, 'material/brush', (result) => `Материал перенесён на ${result.painted?.length ?? 0} мод.`);
}

export function useAutoRow(modelId: string | null | undefined) {
  return useLayoutMutation(modelId, 'auto-row', (result) => `Ряд собран: ${result.moves?.length ?? 0} мод.`);
}

export function useOptimizeRoom(modelId: string | null | undefined) {
  return useLayoutMutation(modelId, 'optimize-room', (result) => (
    result.unplaced?.length ? `Расставлено, не вошло: ${result.unplaced.length}` : 'Модули расставлены по комнате'
  ));
}

function markModelShellStale(qc: ReturnType<typeof useQueryClient>, modelId: string) {
  qc.invalidateQueries({ queryKey: queryKeys.models, refetchType: 'none' });
  qc.invalidateQueries({ queryKey: queryKeys.history(modelId), refetchType: 'none' });
}

function invalidateModuleRuntime(qc: ReturnType<typeof useQueryClient>, modelId: string) {
  qc.invalidateQueries({ queryKey: queryKeys.bundle(modelId) });
  qc.invalidateQueries({ queryKey: queryKeys.analysis(modelId), refetchType: 'none' });
  qc.invalidateQueries({ queryKey: queryKeys.validation(modelId), refetchType: 'none' });
  qc.invalidateQueries({ queryKey: queryKeys.productionReadiness(modelId), refetchType: 'none' });
  qc.invalidateQueries({ queryKey: ['module-operations', modelId], refetchType: 'none' });
  qc.invalidateQueries({ queryKey: queryKeys.bundleGraphs(modelId) });
  qc.invalidateQueries({ queryKey: queryKeys.moduleGraph(modelId) });
  qc.invalidateQueries({ queryKey: queryKeys.autoDimensions(modelId) });
  markModelShellStale(qc, modelId);
}

// Item-scoped fill operations move/edit an existing detail (slider drag, ±1
// facade, thickness blur, shelf→panel, delete). They don't add/remove geometry
// across the module the way structural ops (facades/drawers/sliding/scheme/
// clear_slot) do, and the plan delta is predictable — so they can be applied
// optimistically to the cache AND their bundle refetch (= full scene teardown
// + rebuild) can be coalesced across a burst of edits.
const ITEM_SCOPED_FILL_OPS: ReadonlySet<ModuleFillOperationPayload['operation']> = new Set([
  'update',
  'move',
  'set_rule',
  'convert_shelf',
  'delete_item',
]);

function isItemScopedFillOp(op: ModuleFillOperationPayload['operation']): boolean {
  return ITEM_SCOPED_FILL_OPS.has(op);
}

// Fields carried by an `update` op that map 1:1 onto a plan item, so the cache
// can reflect the edit before the server responds.
const OPTIMISTIC_UPDATE_FIELDS = [
  'thicknessMm',
  'columns',
  'rows',
  'count',
  'panels',
  'frontSpec',
  'frontRatios',
  'material',
  'materialProfile',
  'mount',
  'facadeProfile',
  'openMode',
  'slideSystem',
  'softClose',
  'mechType',
  'hinge',
] as const;

function mapFillItem(
  prev: ModuleFillState,
  itemId: string,
  updater: (item: ModuleFillItem) => ModuleFillItem,
): ModuleFillState {
  let changed = false;
  const items = prev.items.map((it) => {
    if (it.id !== itemId) return it;
    changed = true;
    return updater(it);
  });
  return changed ? { ...prev, items } : prev;
}

function deleteFillItem(prev: ModuleFillState, itemId: string): ModuleFillState {
  const items = prev.items.filter((it) => it.id !== itemId);
  if (items.length === prev.items.length) return prev;
  const slots = prev.slots.map((s) => ({
    ...s,
    itemIds: s.itemIds.filter((id) => id !== itemId),
    leafItemIds: s.leafItemIds?.filter((id) => id !== itemId),
    structuralItemIds: s.structuralItemIds?.filter((id) => id !== itemId),
  }));
  return {
    ...prev,
    items,
    slots,
    summary: { ...prev.summary, items: Math.max(0, (prev.summary?.items ?? items.length + 1) - 1) },
  };
}

/**
 * Apply an item-scoped fill operation to a cached plan, returning a new plan (or
 * the same reference if nothing could be patched). Only the predictable deltas
 * are handled — anything else returns `prev` unchanged so the caller falls back
 * to the server round-trip. Exported for unit testing.
 */
export function optimisticFillPatch(
  prev: ModuleFillState,
  body: ModuleFillOperationPayload,
): ModuleFillState {
  const itemId = body.itemId;
  if (!itemId) return prev;
  if (body.operation !== 'delete_item' && !prev.items.some((it) => it.id === itemId)) return prev;

  switch (body.operation) {
    case 'delete_item':
      return deleteFillItem(prev, itemId);
    case 'move': {
      if (typeof body.ratio !== 'number') return prev;
      const ratio = body.ratio;
      return mapFillItem(prev, itemId, (it) => ({ ...it, ratio }));
    }
    case 'set_rule': {
      if (!body.mode) return prev;
      const mode = body.mode;
      const value = body.value;
      return mapFillItem(prev, itemId, (it) => ({
        ...it,
        rule: { mode, ...(typeof value === 'number' ? { value } : {}) },
      }));
    }
    case 'convert_shelf': {
      const target = body.shelfType ?? body.to;
      if (target !== 'removable_shelf' && target !== 'fixed_panel' && target !== 'rafix_shelf') return prev;
      const shelfType = target === 'fixed_panel' ? 'fixed_panel' : 'removable_shelf';
      const installation = target === 'fixed_panel' ? 'fixed' : 'removable';
      return mapFillItem(prev, itemId, (it) => ({ ...it, shelfType, installation }));
    }
    case 'update': {
      const src = body as Record<string, unknown>;
      const patch: Record<string, unknown> = {};
      for (const field of OPTIMISTIC_UPDATE_FIELDS) {
        if (src[field] !== undefined) patch[field] = src[field];
      }
      if (Object.keys(patch).length === 0) return prev;
      return mapFillItem(prev, itemId, (it) => ({ ...it, ...(patch as Partial<ModuleFillItem>) }));
    }
    default:
      return prev;
  }
}

// Coalesce the expensive geometry-derived refetch that item-scoped fill edits
// trigger. The bundle refetch tears down and procedurally rebuilds the WHOLE
// 3D scene (useGltfLoader keys its rebuild off the `bundle` reference), so a
// slider drag / rapid ±1 clicks would otherwise rebuild the scene on every
// intermediate step. We debounce per-model: the scene rebuilds ONCE, ~300ms
// after the last edit settles. The plan cache (module-fill) is patched
// optimistically + reconciled from the server response immediately, so the
// flyout editor stays live during the debounce window.
const GEOMETRY_REFETCH_DEBOUNCE_MS = 300;
const pendingGeometryRefetch = new Map<string, ReturnType<typeof setTimeout>>();

function cancelPendingGeometryRefetch(modelId: string) {
  const handle = pendingGeometryRefetch.get(modelId);
  if (handle !== undefined) {
    clearTimeout(handle);
    pendingGeometryRefetch.delete(modelId);
  }
}

function refetchGeometryDerived(qc: ReturnType<typeof useQueryClient>, modelId: string) {
  qc.invalidateQueries({ queryKey: queryKeys.bundle(modelId) });
  qc.invalidateQueries({ queryKey: queryKeys.estimate(modelId) });
  qc.invalidateQueries({ queryKey: queryKeys.cutlist(modelId) });
  qc.invalidateQueries({ queryKey: queryKeys.autoDimensions(modelId) });
  qc.invalidateQueries({ queryKey: queryKeys.bundleGraphs(modelId) });
  qc.invalidateQueries({ queryKey: queryKeys.moduleGraph(modelId) });
}

function scheduleGeometryRefetch(qc: ReturnType<typeof useQueryClient>, modelId: string) {
  cancelPendingGeometryRefetch(modelId);
  const handle = setTimeout(() => {
    pendingGeometryRefetch.delete(modelId);
    refetchGeometryDerived(qc, modelId);
  }, GEOMETRY_REFETCH_DEBOUNCE_MS);
  pendingGeometryRefetch.set(modelId, handle);
}

function invalidateModuleFill(
  qc: ReturnType<typeof useQueryClient>,
  modelId: string,
  moduleId: string,
  data?: unknown,
  opts: { scoped?: boolean } = {},
) {
  if (data) {
    rememberCommittedVersion(modelId, data);
    announceOwnModuleCommand(modelId, data);
  }
  if (data) qc.setQueryData(queryKeys.moduleFill(modelId, moduleId), data);
  void qc.invalidateQueries({ queryKey: queryKeys.moduleAtelier(modelId, moduleId) });
  // Non-refetching stale marks — cheap, no scene teardown. Same for structural
  // and item-scoped edits.
  qc.invalidateQueries({ queryKey: queryKeys.analysis(modelId), refetchType: 'none' });
  qc.invalidateQueries({ queryKey: queryKeys.validation(modelId), refetchType: 'none' });
  qc.invalidateQueries({ queryKey: queryKeys.productionReadiness(modelId), refetchType: 'none' });
  qc.invalidateQueries({ queryKey: ['module-operations', modelId], refetchType: 'none' });
  // A fill op rebuilds niche connectivity + writes a history revision, so the
  // version list is stale too. These were missing — only the scheme mutation
  // patched them inline (now redundant).
  qc.invalidateQueries({ queryKey: queryKeys.history(modelId), refetchType: 'none' });
  markModelShellStale(qc, modelId);
  qc.invalidateQueries({ queryKey: queryKeys.moduleFill(modelId, moduleId), refetchType: 'none' });

  if (opts.scoped) {
    // Item-scoped: the plan cache already carries server truth (setQueryData
    // above). Coalesce the heavy geometry refetch (bundle → full scene rebuild,
    // estimate, cutlist, connectivity graphs) so a burst of edits rebuilds the
    // scene once on settle instead of on every step.
    scheduleGeometryRefetch(qc, modelId);
    return;
  }
  // Structural op (facades/drawers/sliding/scheme/clear_slot/add-item): geometry
  // changes across the module — refetch immediately for a correct scene, and
  // drop any pending coalesced refetch since this one supersedes it.
  cancelPendingGeometryRefetch(modelId);
  refetchGeometryDerived(qc, modelId);
}

export function useUpdatePanel(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async ({ panelId, patch }: { panelId: string; patch: PanelPatchBody }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cachedBundle = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cachedBundle?.bundleVersion);
        const result = await postJson<{
          ok: boolean;
          panelId: string;
          objectUpdated: boolean;
          projectionRegenerated: boolean;
          changedFields: string[];
          bundleVersion?: number;
          bundleHash?: string;
        }>(
          `/api/models/${encodeURIComponent(modelId)}/panels/${encodeURIComponent(panelId)}`,
          { ...patch, expectedBundleVersion: expected },
        );
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
      toast.success(`Деталь обновлена`, {
        description: `${data.panelId}${data.objectUpdated ? ' · объект' : ''}${data.projectionRegenerated ? ' · смета обновлена' : ''}`,
      });
    },
    onError: (err) => toast.error('Не удалось применить', { description: String(err) }),
  });
}

export function useBulkUpdatePanels(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async ({ panelIds, patch }: { panelIds: string[]; patch: PanelPatchBody }) => {
      if (!modelId) throw new Error('No active model');
      if (!panelIds.length) throw new Error('No panels selected');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = await postJson<{
          ok: boolean;
          applied: number;
          total: number;
          bundleVersion: number;
          results: {
            panelId: string;
            ok: boolean;
            objectUpdated?: boolean;
            changedFields?: string[];
            error?: string;
          }[];
        }>(`/api/models/${encodeURIComponent(modelId)}/panels/bulk`, {
          panelIds,
          patch,
          expectedBundleVersion: expected,
        });
        if (!result.ok || result.applied <= 0) {
          /* Отказ называет ВСЕ причины, а не первую попавшуюся: у дюжины деталей их обычно
             несколько, и по первой человек чинит не то. */
          const report = bulkApplyReport(result.results, result.applied, result.total);
          const reason = report.skipped.length
            ? describeBulkApplyReport(report)
            : 'Сервер не применил изменение ни к одной панели';
          throw new Error(reason);
        }
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
      /* «Часть панелей не была затронута» — это не отчёт, а пересказ вычитания. Причины уже
         пришли с сервера в `results[].error`; раньше их выбрасывали.
         Донор: PRO100-онлайн, src/ui/BulkEditPanel.tsx. */
      toast.success(`Изменено ${data.applied}/${data.total} деталей`, {
        description: describeBulkApplyReport(bulkApplyReport(data.results, data.applied, data.total)),
      });
    },
    onError: (err) => toast.error('Групповое изменение не выполнено', { description: String(err) }),
  });
}

export type BulkSelectedReplacementCommand = {
  schema: 'holzcom.bulk_selected_replacement.v2';
  selectedEntityIds: string[];
  replacement:
    | { kind: 'material'; fromMaterialId: number; toMaterialId: number }
    | { kind: 'edge'; side: 'L' | 'R' | 'T' | 'B'; from: unknown; to: Record<string, unknown> }
    | { kind: 'handle' | 'hinge'; fromArticle: string; to: { catalogId: number; sourceReleaseSha: string; rowSha256: string } };
};

export type BulkSelectedReplacementPreview = {
  schema: 'holzcom.bulk_selected_replacement_preview.v1';
  sourceBundleSha256: string;
  commandSha256: string;
  previewSha256: string;
  selectedEntityIds: string[];
  touchedIds: string[];
  untouched: { entityId: string; reason: string }[];
  changes: { entityId: string; old: unknown; new: unknown }[];
};

export function useBulkSelectedReplacement(modelId: string | null | undefined) {
  const qc = useQueryClient();
  const preview = useOwnedMutation(modelId, {
    mutationFn: async (command: BulkSelectedReplacementCommand) => {
      if (!modelId) throw new Error('No active model');
      return postJson<{ ok: true; modelId: string; bundleVersion: number; preview: BulkSelectedReplacementPreview }>(
        `/api/models/${encodeURIComponent(modelId)}/bulk-selected-replacement/preview`, { command },
      );
    },
    onError: (err) => toast.error('Предпросмотр замены не построен', { description: String(err) }),
  });
  const apply = useOwnedMutation(modelId, {
    mutationFn: async ({ command, preview: exactPreview }: { command: BulkSelectedReplacementCommand; preview: BulkSelectedReplacementPreview }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = await postJson<{ ok: true; status: 'APPLIED' | 'ALREADY_APPLIED'; changed: boolean; touchedIds?: string[]; bundleVersion?: number; bundleHash?: string }>(
          `/api/models/${encodeURIComponent(modelId)}/bulk-selected-replacement/apply`,
          { command, preview: exactPreview, expectedBundleVersion: expected },
        );
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
      toast.success(data.status === 'ALREADY_APPLIED' ? 'Замена уже применена' : `Изменено: ${data.touchedIds?.length ?? 0}`);
    },
    onError: (err) => toast.error('Массовая замена не выполнена', { description: String(err) }),
  });
  return { preview, apply };
}

export function useRemoveModuleInstance(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (instanceId: string) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = await api.removeModuleInstance(modelId, instanceId, expected, true);
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
      toast.success('Модуль удалён из проекта', {
        description: `${data.entitiesRemoved ?? 0} объектов · ${data.rowsRemoved ?? 0} строк`,
      });
    },
    onError: (err) => toast.error('Не удалось удалить модуль', { description: String(err) }),
  });
}

export function useDuplicateModuleInstance(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (instanceId: string) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = await api.duplicateModuleInstance(modelId, instanceId, expected, true);
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
      /* ВОЛНА W: копия вырастает на месте (easeOutBack + bloom-вспышка), а не
       * возникает мгновенным телепортом рядом с оригиналом. */
      if (data.instanceId) announcePlacementEffect({ kind: 'appear', entityIds: [data.instanceId] }, modelId);
      toast.success('Модуль продублирован', { description: data.instanceId });
    },
    onError: (err) => toast.error('Не удалось продублировать модуль', { description: String(err) }),
  });
}

/** Объединить шкафы в один корпус с секциями. Передаётся один seed-id —
 *  бэкенд сам подхватывает весь вплотную стоящий по X ряд (без мультивыбора). */
export function useCombineModulesIntoSections(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async ({ instanceId, name }: { instanceId: string; name?: string }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = await api.combineModulesIntoSections(modelId, [instanceId], expected, name, true);
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
      toast.success('Шкафы объединены в один корпус с секциями', { description: data.instanceId });
    },
    onError: (err) => toast.error('Объединение не выполнено', { description: String(err) }),
  });
}

/** Разъединить корпус с секциями на отдельные корпуса. */
export function useSplitModuleIntoCorpora(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (instanceId: string) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = await api.splitModuleIntoCorpora(modelId, instanceId, expected, true);
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
      toast.success('Корпус разъединён', { description: `Создано корпусов: ${data.instances?.length ?? 0}` });
    },
    onError: (err) => toast.error('Разъединение не выполнено', { description: String(err) }),
  });
}

type CompositionInstanceRef = { instanceId: string; catalogId: string; name?: string };

/** Read the {instanceId → catalogId/name} map out of a bundle's module
 *  composition hint.  This is the only source that ties a placed instance back
 *  to the catalog product it was composed from — which is what a cross-model
 *  paste needs, since the target model has no knowledge of the source instance. */
function extractCompositionInstances(
  bundle: UniversalBundle | null | undefined,
): Map<string, CompositionInstanceRef> {
  const out = new Map<string, CompositionInstanceRef>();
  const instances = bundle?.canonicalScene?.scene?.viewerHints?.moduleComposition?.instances;
  if (!Array.isArray(instances)) return out;
  for (const item of instances) {
    const instanceId = String((item as { instanceId?: unknown })?.instanceId ?? '').trim();
    const catalogId = String((item as { catalogId?: unknown })?.catalogId ?? '').trim();
    if (!instanceId || !catalogId) continue;
    const rawName = (item as { name?: unknown })?.name;
    out.set(instanceId, { instanceId, catalogId, name: typeof rawName === 'string' ? rawName : undefined });
  }
  return out;
}

type PasteModulesResult = {
  mode: 'duplicate' | 'cross-model';
  requested: number;
  pasted: number;
  instanceIds: string[];
  unresolved: number;
};

/**
 * Paste the module ids sitting in `viewerStore.moduleClipboard` into
 * `targetModelId`.  Two backend paths, chosen by where the copy came from:
 *
 *  • Same model  → POST …/instances/{id}/duplicate for each id (the existing,
 *    tested duplicate route).  This is the multi-selection dupe.
 *  • Other model → resolve each instance's catalogId+name from the SOURCE
 *    bundle's moduleComposition hint and POST …/instances/batch (the library/
 *    catalog add path).  No new backend route is introduced.
 *
 * Cross-model paste therefore re-composes the modules from their catalog in the
 * target model.  Limitation: only instances that are composed *library*
 * instances (carry a catalogId in moduleComposition) can cross models; a
 * hand-authored / imported module with no catalogId is skipped and counted in
 * `unresolved`.  Session-level module edits (resize/fill) on the source are NOT
 * carried across — the paste places a fresh catalog instance.
 */
export function usePasteModules(targetModelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(targetModelId, {
    mutationFn: async (): Promise<PasteModulesResult> => {
      if (!targetModelId) throw new Error('No active model');
      const clip = useViewerStore.getState().moduleClipboard;
      if (!clip || clip.moduleIds.length === 0) throw new Error('Буфер модулей пуст');
      const requested = clip.moduleIds.length;

      if (clip.sourceModelId === targetModelId) {
        /* СЕКЦИЯ ВСТАЁТ СЕКЦИЕЙ. Раньше здесь шёл дубль НА КАЖДЫЙ модуль, а дубль ставит
           копию рядом со СВОИМ оригиналом (или в первое свободное место комнаты): тумба +
           варочная + короб после вставки оказывались тремя копиями в разных местах, и
           взаимное расположение — то единственное, ради чего секцию копировали, — терялось.
           Теперь это ОДНА серверная команда: одна запись в истории, одна отмена, и половине
           секции негде появиться. Донор: PRO100-онлайн, src/app/module-clipboard.ts.

           Эта развязка уже была и пропала в слиянии efc5053f8 (github/main): серверная
           ручка, клиент `api.pasteModuleSection`, типы и пины остались, а ЕДИНСТВЕННЫЙ
           потребитель откатился на поштучный дубль — возможность стала недостижимой. */
        return enqueueModelCommand(targetModelId, async () => {
          const res = await api.pasteModuleSection(targetModelId, {
            sourceInstanceIds: clip.moduleIds,
            createSnapshot: true,
            expectedBundleVersion: requiredCommandBaseline(
              targetModelId,
              qc.getQueryData<UniversalBundle>(queryKeys.bundle(targetModelId))?.bundleVersion,
            ),
          });
          rememberCommittedVersion(targetModelId, res);
          const instanceIds = res.instanceIds ?? [];
          return { mode: 'duplicate' as const, requested, pasted: instanceIds.length, instanceIds, unresolved: 0 };
        });
      }

      // Cross-model: pull catalogId+name from the source bundle (cache first,
      // else fetch it) and batch-add into the target model.
      let sourceBundle = qc.getQueryData<UniversalBundle>(queryKeys.bundle(clip.sourceModelId)) ?? null;
      if (!sourceBundle) sourceBundle = await api.bundle(clip.sourceModelId);
      const refs = extractCompositionInstances(sourceBundle);
      const items = clip.moduleIds
        .map((id) => refs.get(id))
        .filter((ref): ref is CompositionInstanceRef => Boolean(ref))
        .map((ref) => ({ catalogId: ref.catalogId, name: ref.name }));
      if (items.length === 0) {
        throw new Error('Не удалось определить каталог модулей для вставки в другую модель');
      }
      const res = await api.addModuleInstancesBatch(targetModelId, {
        items,
        createSnapshot: true,
        snapshotLabel: `modules.paste:${items.length}`,
        expectedBundleVersion: requiredCommandBaseline(
          targetModelId,
          qc.getQueryData<UniversalBundle>(queryKeys.bundle(targetModelId))?.bundleVersion,
        ),
      });
      rememberCommittedVersion(targetModelId, res);
      const instanceIds = (res.added ?? []).map((a) => a.instanceId).filter(Boolean);
      return {
        mode: 'cross-model',
        requested,
        pasted: instanceIds.length,
        instanceIds,
        unresolved: requested - items.length,
      };
    },
    onSuccess: (data) => {
      if (targetModelId) invalidateModel(qc, targetModelId);
      /* ВОЛНА W: вставленные модули появляются каскадом (стагер внутри appear),
       * но только в ОТКРЫТОЙ модели — фоновому канвасу чужая анимация не нужна. */
      if (data.instanceIds.length && useViewerStore.getState().activeModelId === targetModelId) {
        announcePlacementEffect({ kind: 'appear', entityIds: data.instanceIds }, targetModelId);
        // Highlight the freshly pasted instances — but only if the target model is
        // the one currently open (cross-model paste into a background model must
        // not steal the viewer's selection).
        useViewerStore.getState().setSelectedMany(data.instanceIds);
        // Match the visible PRO100 sequence: all new copies stay selected and
        // the last landed copy receives the inspector/gizmo focus.
        useViewerStore.getState().setActiveModule(data.instanceIds[data.instanceIds.length - 1]);
      }
      const suffix = data.unresolved > 0 ? ` · ${data.unresolved} без каталога пропущено` : '';
      const scope = data.mode === 'cross-model' ? 'между моделями' : 'дубль в модели';
      toast.success(data.pasted > 1 ? `Вставлено модулей: ${data.pasted}` : 'Модуль вставлен', {
        description: `${scope}${suffix}`,
      });
    },
    onError: (err) => toast.error('Не удалось вставить модули', { description: String(err) }),
  });
}

export function useMirrorModule(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async ({ instanceId, axis }: { instanceId: string; axis?: 'x' | 'z' }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.mirrorModule(
          modelId,
          instanceId,
          axis ?? 'x',
          requiredCommandBaseline(modelId, cached?.bundleVersion),
        );
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data, variables) => {
      if (modelId) invalidateModel(qc, modelId, data);
      /* ВОЛНА W: модуль остаётся на месте — не «появление», а короткая встряска
       * масштабом + bloom-вспышка, чтобы зеркало читалось как действие, а не подмена. */
      announcePlacementEffect({ kind: 'module-settle', moduleId: variables.instanceId }, modelId);
      toast.success('Модуль отзеркалён', { description: `${data.partsMirrored} деталей` });
    },
    onError: (err) => toast.error('Не удалось отзеркалить', { description: String(err) }),
  });
}

export function useRotateModule(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async ({ instanceId, rotationYDeg, deltaYDeg }: {
      instanceId: string;
      rotationYDeg?: number;
      deltaYDeg?: number;
    }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.rotateModule(modelId, instanceId, {
          rotationYDeg,
          deltaYDeg,
          expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
        });
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
      toast.success('Модуль повёрнут', { description: `${Math.round(data.rotationYDeg ?? 0)}°` });
    },
    onError: (err) => toast.error('Не удалось повернуть модуль', { description: String(err) }),
  });
}

/** Persist a physical entity move through the same per-model OCC queue. */
export function useMoveEntity(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { entityId: string; dx: number; dy: number; dz: number }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.moveEntity(modelId, payload.entityId, {
          dx: payload.dx,
          dy: payload.dy,
          dz: payload.dz,
          expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
        });
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
    },
  });
}

export function useSetModuleConstraints(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async ({ instanceId, reveal, handle }: {
      instanceId: string;
      reveal?: number | string;
      handle?: { edge?: string; offsetMm?: number | string } | Record<string, never>;
    }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.setModuleConstraints(modelId, instanceId, {
          reveal,
          handle,
          expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
        });
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
      toast.success('Параметры применены');
    },
    onError: (err) => toast.error('Не удалось применить параметры', { description: String(err) }),
  });
}

export function useReplaceModuleInstance(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async ({ instanceId, catalogId, name }: { instanceId: string; catalogId: string; name?: string }, { invocationOwner }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = await api.replaceModuleInstance(modelId, instanceId, {
          catalogId, name, createSnapshot: true, expectedBundleVersion: expected,
        });
        rememberCommittedVersion(modelId, result);
        return result;
      }, invocationOwner);
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
      toast.success('Модуль заменён', { description: data.instanceId });
    },
    onError: (err) => toast.error('Не удалось заменить модуль', { description: String(err) }),
  }, true);
}

export function useBakeModuleEdits(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async () => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = await api.bakeModuleEdits(modelId, expected, true);
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
      toast.success(data.changed ? 'Правки запечены в модель' : 'Нет незапечённых правок');
    },
    onError: (err) => toast.error('Не удалось применить правки в модель', { description: String(err) }),
  });
}

export function useResetPanel(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (panelId: string) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = await postJson<{ ok: boolean; panelId: string; bundleVersion: number }>(
          `/api/models/${encodeURIComponent(modelId)}/panels/${encodeURIComponent(panelId)}/reset`,
          { expectedBundleVersion: expected },
        );
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: () => {
      if (modelId) invalidateModel(qc, modelId);
      toast.success('Деталь сброшена к исходной');
    },
    onError: (err) => toast.error('Не удалось сбросить', { description: String(err) }),
  });
}

export function useUndo(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async () => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = await postJson<{ ok: boolean; reason?: string; cursor: number; versionId?: string; bundleVersion?: number }>(
          `/api/models/${encodeURIComponent(modelId)}/history/undo?expectedVersion=${expected}`,
        );
        if (result.ok) rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (!modelId) return;
      if (!data.ok) {
        toast.message('Undo недоступен', { description: data.reason || '' });
        return;
      }
      invalidateModel(qc, modelId, data);
      /* ВОЛНА W: вернувшиеся сущности появляются мягко, без вспышки — отмена
       * не праздник, а возврат к прежнему. Иды неизвестны до перечитывания
       * бандла, сцену кормит диффом сам хук (kind: 'appear-returning'). */
      announcePlacementEffect({ kind: 'appear-returning' }, modelId);
      toast.success('Отмена применена', { description: `Версия ${data.versionId ?? '?'}` });
    },
    onError: (err) => toast.error('Не удалось отменить', { description: String(err) }),
  });
}

export function useRedo(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async () => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = await postJson<{ ok: boolean; reason?: string; cursor: number; versionId?: string; bundleVersion?: number }>(
          `/api/models/${encodeURIComponent(modelId)}/history/redo?expectedVersion=${expected}`,
        );
        if (result.ok) rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (!modelId) return;
      if (!data.ok) {
        toast.message('Redo недоступен', { description: data.reason || '' });
        return;
      }
      invalidateModel(qc, modelId, data);
      /* ВОЛНА W: возврат — то же мягкое появление без вспышки, что и отмена. */
      announcePlacementEffect({ kind: 'appear-returning' }, modelId);
      toast.success('Возврат применён', { description: `Версия ${data.versionId ?? '?'}` });
    },
    onError: (err) => toast.error('Не удалось вернуть', { description: String(err) }),
  });
}

export function useRestoreVersion(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (selection: string | {
      versionId: string;
      expectedSnapshotSha256?: string;
      /** W4, таймлапс: покадровый restore без тоста на каждый кадр — фильм
       *  сообщает о себе сам (прогресс плеера), двадцать тостов фильму не нужны. */
      quiet?: boolean;
    }) => {
      const versionId = typeof selection === 'string' ? selection : selection.versionId;
      const snapshotHash = typeof selection === 'string' ? undefined : selection.expectedSnapshotSha256;
      const snapshotQuery = snapshotHash ? `&expectedSnapshotSha256=${encodeURIComponent(snapshotHash)}` : '';
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = await postJson<{ ok: boolean; cursor: number; versionId: string; bundleVersion: number }>(
          `/api/models/${encodeURIComponent(modelId)}/history/restore/${encodeURIComponent(versionId)}?expectedVersion=${expected}${snapshotQuery}`,
        );
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data, selection) => {
      if (modelId) invalidateModel(qc, modelId, data);
      const quiet = typeof selection === 'string' ? false : Boolean(selection.quiet);
      if (!quiet) toast.success('Версия восстановлена', { description: data.versionId });
    },
    onError: (err, selection) => {
      const quiet = typeof selection === 'string' ? false : Boolean(selection.quiet);
      if (!quiet) toast.error('Не удалось восстановить', { description: String(err) });
    },
  });
}

/* A13: именованный чекпоинт — явная точка возврата среди подавленных
 * промежуточных снапшотов мульти-шаговых workflows (recognition, наполнение,
 * аудиты фасадов). Сервер: POST /history/checkpoint {label} (extra=forbid —
 * никакого expectedVersion), запись попадает в ленту истории со своим label. */
export function useCreateCheckpoint(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (label: string) => {
      if (!modelId) throw new Error('No active model');
      const trimmed = label.trim();
      if (!trimmed) throw new Error('checkpoint label is required');
      return enqueueModelCommand(modelId, async () => postJson<{
        ok: boolean; modelId: string; cursor: number; versionId: string; label: string;
      }>(
        `/api/models/${encodeURIComponent(modelId)}/history/checkpoint`,
        { label: trimmed },
      ));
    },
    onSuccess: (data) => {
      if (modelId) void qc.invalidateQueries({ queryKey: queryKeys.history(modelId) });
      toast.success(`Чекпоинт «${data.label}» сохранён`, { description: data.versionId });
    },
    onError: (err) => toast.error('Не удалось сохранить чекпоинт', { description: String(err) }),
  });
}

export function useResizeModule(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: ModuleResizePayload & { commandContext?: ModelCommandContext }, { invocationOwner }) => {
      if (!modelId) throw new Error('No active model');
      if (payload.commandContext) requireModelCommandContext(payload.commandContext, modelId);
      announceModuleCommandTrace(modelId, 'resize', 'queued');
      return enqueueModelCommand(modelId, async () => {
        announceModuleCommandTrace(modelId, 'resize', 'dequeued');
        if (payload.commandContext) requireModelCommandContext(payload.commandContext, modelId);
        const { instanceId, commandContext: _commandContext, ...body } = payload;
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        announceModuleCommandTrace(modelId, 'resize', 'request_started');
        const authGeneration = authenticatedCacheGeneration();
        const request = fetch(
          `/api/models/${encodeURIComponent(modelId)}/modules/${encodeURIComponent(instanceId)}/resize`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...body, expectedBundleVersion: expected }),
            signal: AbortSignal.timeout(180_000),
          },
        );
        // fetch() has returned its promise and handed the request to the
        // browser transport. Uvicorn writes its access line only after the
        // expensive Graph2 rebuild responds, so this phase distinguishes a
        // dispatched long-running command from a frontend queue stall.
        announceModuleCommandTrace(modelId, 'resize', 'fetch_dispatched');
        const r = await request;
        requireAuthenticatedGeneration(authGeneration);
        if (!r.ok) {
          const text = await r.text();
          try {
            const parsed = JSON.parse(text);
            const detail = parsed?.detail;
            if (detail) {
              const error = new Error(`${r.status} ${detail.message ?? detail.code ?? text}`);
              (error as Error & { detail?: unknown }).detail = detail;
              throw error;
            }
          } catch (err) {
            if (err instanceof Error && err.message.startsWith(`${r.status} `)) throw err;
          }
          throw new Error(`${r.status} ${text}`);
        }
        announceModuleCommandTrace(modelId, 'resize', 'response_received');
        const result = await r.json() as {
          ok: boolean; instanceId: string;
          oldDims: { w: number; h: number; d: number };
          newDims: { w: number; h: number; d: number };
          scale: { x: number; y: number; z: number };
          childrenUpdated: number;
          bundleVersion: number;
        };
        requireAuthenticatedGeneration(authGeneration);
        rememberCommittedVersion(modelId, result);
        return result;
      }, invocationOwner);
    },
    onSuccess: (data, payload) => {
      const ownerId = payload.commandContext?.modelId ?? modelId;
      if (ownerId) announceModuleCommandTrace(ownerId, 'resize', 'settled');
      // The PATCH is the command boundary. Query refetches refresh secondary
      // panels in the background; making mutateAsync await every one of them
      // leaves the resize HUD stuck in "saving" when any unrelated endpoint
      // is slow, even though the canonical bundle has already committed.
      if (ownerId) void invalidateModel(qc, ownerId, data);
      toast.success(
        `Resize: ${Math.round(data.oldDims.w)}×${Math.round(data.oldDims.h)}×${Math.round(data.oldDims.d)} → ${Math.round(data.newDims.w)}×${Math.round(data.newDims.h)}×${Math.round(data.newDims.d)} мм`,
        { description: `${data.childrenUpdated} элементов наполнения пересчитано` },
      );
    },
    onError: (err) => {
      if (modelId) announceModuleCommandTrace(modelId, 'resize', 'error');
      toast.error('Resize не удался', { description: String(err) });
    },
  }, true);
}

export function useReplaceConnectorPlane(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: {
      moduleId: string;
      system: ConnectorSystem;
      targetPanelId?: string | null;
      dryRun?: boolean;
      createSnapshot?: boolean;
    }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const { moduleId, ...body } = payload;
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.replaceConnectorPlane(modelId, moduleId, {
          ...body,
          expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
        });
        if (result.persisted) rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data: ReplaceConnectorPlaneResult) => {
      if (modelId && data.persisted) {
        invalidateModel(qc, modelId);
      }
      if (data.dryRun) {
        toast.info('Проверка соединителей пройдена', {
          description: `${Math.round(data.after.holes ?? 0)} отверстий · ${Math.round(data.after.panelIds?.length ?? 0)} деталей`,
        });
        return;
      }
      toast.success('Система соединителей применена', {
        description: `${labelConnectorSystem(data.system)} · ${Math.round(data.after.holes ?? 0)} отверстий`,
      });
    },
    onError: (err) => toast.error('Не удалось заменить соединители', { description: String(err) }),
  });
}

export function useReplaceModuleDetailVariant(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: {
      moduleId: string;
      role: string;
      variant: string;
      createSnapshot?: boolean;
    }) => {
      if (!modelId) throw new Error('No active model');
      const { moduleId, ...body } = payload;
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.replaceModuleDetailVariant(modelId, moduleId, {
          ...body,
          expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
        });
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
      toast.success('Тип детали заменён', {
        description: `${data.role} → ${data.variant}`,
      });
    },
    onError: (err) => toast.error('Не удалось заменить тип детали', { description: String(err) }),
  });
}

/**
 * Rebuild is a topology-changing aggregate command.  Keep the template UI on
 * the same per-model queue and committed-version ledger as every other module
 * edit instead of allowing a stale flyout snapshot to manufacture baseline 0.
 */
export function useRebuildModule(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: Omit<RebuildModuleBody, 'expectedBundleVersion'> & { moduleId: string }) => {
      if (!modelId) throw new Error('No active model');
      const { moduleId, ...body } = payload;
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.rebuildModule(modelId, moduleId, {
          ...body,
          expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
        });
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId, data);
    },
  });
}

export type AddCabinetToRoomBody = Parameters<typeof api.addCabinetToRoom>[2];
export type AddModuleToRoomBody = Parameters<typeof api.addModuleToRoom>[2];
export type FitRunToRoomBody = Parameters<typeof api.fitRunToRoom>[2];

/** Room construction uses placement constraints, so it needs the same CAS
 * queue as module edits: a stale room plan must never be replayed. */
export function useAddCabinetToRoom(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: Omit<AddCabinetToRoomBody, 'expectedVersion' | 'commandId' | 'idempotencyKey'> & { roomCode: string }) => {
      if (!modelId) throw new Error('No active model');
      const { roomCode, ...body } = payload;
      const commandId = `cabinet-add:${crypto.randomUUID()}`;
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.addCabinetToRoom(modelId, roomCode, {
          ...body,
          commandId,
          idempotencyKey: commandId,
          expectedVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
        });
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => { if (modelId) invalidateModel(qc, modelId, data); },
  });
}

export function useAddModuleToRoom(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: Omit<AddModuleToRoomBody, 'expectedVersion'> & { roomCode: string }) => {
      if (!modelId) throw new Error('No active model');
      const { roomCode, ...body } = payload;
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.addModuleToRoom(modelId, roomCode, {
          ...body,
          expectedVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
        });
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => { if (modelId) invalidateModel(qc, modelId, data); },
  });
}

export function useReorderComposition(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { compositionId: string; moduleIds: string[] }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.reorderComposition(modelId, payload.compositionId, {
          moduleIds: payload.moduleIds,
          expectedVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
        });
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => { if (modelId) invalidateModel(qc, modelId, data); },
  });
}

/** Versioned composition lifecycle commands share the same per-model queue. */
function compositionCommand<T>(
  modelId: string,
  qc: ReturnType<typeof useQueryClient>,
  command: (expectedVersion: number) => Promise<T>,
): Promise<T> {
  return enqueueModelCommand(modelId, async () => {
    const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
    const result = await command(requiredCommandBaseline(modelId, cached?.bundleVersion));
    rememberCommittedVersion(modelId, result);
    return result;
  });
}

export function useCreateComposition(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { name?: string; roomId?: string; moduleIds: string[]; axisPrimary?: 'x' | 'z' }) => {
      if (!modelId) throw new Error('No active model');
      return compositionCommand(modelId, qc, (expectedVersion) =>
        api.createComposition(modelId, { ...payload, expectedVersion }));
    },
    onSuccess: (data) => { if (modelId) invalidateModel(qc, modelId, data); },
  });
}

export function useUpdateComposition(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { compositionId: string; name?: string; axisPrimary?: 'x' | 'z' }) => {
      if (!modelId) throw new Error('No active model');
      const { compositionId, ...body } = payload;
      return compositionCommand(modelId, qc, (expectedVersion) =>
        api.updateComposition(modelId, compositionId, { ...body, expectedVersion }));
    },
    onSuccess: (data) => { if (modelId) invalidateModel(qc, modelId, data); },
  });
}

export function useDeleteComposition(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (compositionId: string) => {
      if (!modelId) throw new Error('No active model');
      return compositionCommand(modelId, qc, (expectedVersion) =>
        api.deleteComposition(modelId, compositionId, expectedVersion));
    },
    onSuccess: (data) => { if (modelId) invalidateModel(qc, modelId, data); },
  });
}

export function useResizeComposition(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { compositionId: string; wMm?: number; hMm?: number; dMm?: number }) => {
      if (!modelId) throw new Error('No active model');
      const { compositionId, ...body } = payload;
      return compositionCommand(modelId, qc, (expectedVersion) =>
        api.resizeComposition(modelId, compositionId, { ...body, expectedVersion }));
    },
    onSuccess: (data) => { if (modelId) invalidateModel(qc, modelId, data); },
  });
}

export function useAutoGroupCompositions(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async () => {
      if (!modelId) throw new Error('No active model');
      return compositionCommand(modelId, qc, (expectedVersion) =>
        api.autoGroupCompositions(modelId, expectedVersion));
    },
    onSuccess: (data) => { if (modelId) invalidateModel(qc, modelId, data); },
  });
}

export function useFitRunToRoom(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: Omit<FitRunToRoomBody, 'expectedVersion'> & { roomCode: string }) => {
      if (!modelId) throw new Error('No active model');
      const { roomCode, ...body } = payload;
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.fitRunToRoom(modelId, roomCode, {
          ...body,
          expectedVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
        });
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (!modelId) return;
      /* ВОЛНА W2 «волна ряда»: собранный ряд встаёт ВОЛНОЙ — appear стагерит
       * 40 мс на модуль по порядку следования (сервер отдаёт instanceIds в
       * порядке сегментов ряда). Механика — волны W, здесь только анонс. */
      if (data.instanceIds?.length) {
        announcePlacementEffect({ kind: 'appear', entityIds: data.instanceIds }, modelId);
      }
      invalidateModel(qc, modelId, data);
    },
  });
}

export function useMoveToRoom(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { kind: 'module' | 'composition'; entityId: string; roomId: string }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = payload.kind === 'module'
          ? await api.moveModuleToRoom(modelId, payload.entityId, payload.roomId, expected)
          : await api.moveCompositionToRoom(modelId, payload.entityId, payload.roomId, expected);
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => { if (modelId) invalidateModel(qc, modelId, data); },
  });
}

type ReplaceHardwareInstancePayload = HardwareMaterialSelection & {
      hardwareEntityId: string;
      group?: 'hinge' | 'slide' | 'handle' | string;
      createSnapshot?: boolean;
    };

export function useReplaceHardwareInstance(modelId: string | null | undefined) {
  const qc = useQueryClient();
  const mutation = useOwnedMutation(modelId, {
    mutationFn: async (payload: ReplaceHardwareInstancePayload & { commandContext: ModelCommandContext }) => {
      const ownerId = requireModelCommandContext(payload.commandContext, modelId, 'HARDWARE_SELECTION_CONTEXT_STALE');
      const selection = requireHardwareMaterialSelection(payload);
      return enqueueModelCommand(ownerId, async () => {
        requireModelCommandContext(payload.commandContext, ownerId, 'HARDWARE_SELECTION_CONTEXT_STALE');
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(ownerId));
        const result = await api.replaceHardwareInstance(ownerId, {
          hardwareEntityId: payload.hardwareEntityId, group: payload.group, createSnapshot: payload.createSnapshot, ...selection,
          expectedBundleVersion: requiredCommandBaseline(ownerId, cached?.bundleVersion),
        });
        rememberCommittedVersion(ownerId, result);
        return result;
      });
    },
    onSuccess: (data, payload) => {
      invalidateModel(qc, payload.commandContext.modelId!);
      toast.success('Фурнитура заменена', {
        description: String(data.catalog?.name || data.hardwareEntityId),
      });
    },
    onError: (err) => toast.error('Не удалось заменить фурнитуру', { description: String(err) }),
  });
  // Capture before TanStack can pause an offline mutation or replace observer options.
  return {
    ...mutation,
    mutate: (payload: ReplaceHardwareInstancePayload, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate({ ...payload, commandContext: captureModelCommandContext(modelId) }, options),
    mutateAsync: (payload: ReplaceHardwareInstancePayload, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync({ ...payload, commandContext: captureModelCommandContext(modelId) }, options),
  };
}

type ApplyHardwarePresetPayload = HardwareMaterialSelection & {
      group: string;
      useTipOn?: boolean;
      createSnapshot?: boolean;
    };

export function useApplyHardwarePreset(modelId: string | null | undefined) {
  const qc = useQueryClient();
  const mutation = useOwnedMutation(modelId, {
    mutationFn: async (payload: ApplyHardwarePresetPayload & { commandContext: ModelCommandContext }) => {
      const ownerId = requireModelCommandContext(payload.commandContext, modelId, 'HARDWARE_SELECTION_CONTEXT_STALE');
      const selection = requireHardwareMaterialSelection(payload);
      return enqueueModelCommand(ownerId, async () => {
        requireModelCommandContext(payload.commandContext, ownerId, 'HARDWARE_SELECTION_CONTEXT_STALE');
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(ownerId));
        const result = await api.applyHardwarePreset(ownerId, {
          group: payload.group, useTipOn: payload.useTipOn, createSnapshot: payload.createSnapshot, ...selection,
          expectedBundleVersion: requiredCommandBaseline(ownerId, cached?.bundleVersion),
        });
        if (result.ok !== true || !Number.isSafeInteger(result.counts?.instances) || result.counts.instances <= 0) {
          throw new Error('HARDWARE_APPLY_RECEIPT_INVALID');
        }
        rememberCommittedVersion(ownerId, result);
        return result;
      });
    },
    onSuccess: (data, payload) => {
      invalidateModel(qc, payload.commandContext.modelId!, data);
    },
  });
  // Capture before TanStack can pause an offline mutation or replace observer options.
  return {
    ...mutation,
    mutate: (payload: ApplyHardwarePresetPayload, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate({ ...payload, commandContext: captureModelCommandContext(modelId) }, options),
    mutateAsync: (payload: ApplyHardwarePresetPayload, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync({ ...payload, commandContext: captureModelCommandContext(modelId) }, options),
  };
}

export function usePatchRoomMaterials(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: {
      roomCode: string;
      wallMaterialId?: string;
      floorMaterialId?: string;
      ceilingMaterialId?: string;
      skirtMaterialId?: string;
    }) => {
      if (!modelId) throw new Error('No active model');
      const { roomCode, ...body } = payload;
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await requestJson<{ ok: boolean; updated: Record<string, number>; bundleVersion: number; wallMaterialId?: string; floorMaterialId?: string; ceilingMaterialId?: string }>(
          `/api/models/${encodeURIComponent(modelId)}/rooms/${encodeURIComponent(roomCode)}/materials`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...body,
              expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
            }),
          },
        );
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data, payload) => {
      if (modelId) invalidateModel(qc, modelId);
      const changed = Object.entries(data.updated).filter(([, v]) => v > 0)
        .map(([k, v]) => `${k}: ${v}`).join(', ');
      toast.success(`Материалы ${payload.roomCode} обновлены`, { description: changed || '—' });
    },
    onError: (err) => toast.error('Не удалось сменить материал', { description: String(err) }),
  });
}

export type RoomGeometryEdit = {
  roomId: string;
  name: string;
  widthMm: number;
  depthMm: number;
  ceilingHeightMm: number;
  /** At most one opening per wall — the backend rejects a second one on the
   * same wall (ROOM_OPENING_WALL_CONFLICT) rather than silently stacking or
   * overlapping cuts through the same wall box. */
  openings: Array<{
    kind: 'door' | 'window'; wall: 'north' | 'east' | 'south' | 'west';
    offsetMm: number; widthMm: number; bottomMm: number; heightMm: number;
  }>;
  utilities: Array<{
    kind: 'socket' | 'water' | 'drain' | 'gas' | 'hood_duct'; wall: 'north' | 'east' | 'south' | 'west';
    offsetMm: number; heightMm: number;
  }>;
};

/** First PRO100 room-editor slice, serialized through the common model CAS queue. */
export function useUpdateRoomGeometry(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async ({ roomId, ...body }: RoomGeometryEdit) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await requestJson<{ ok: true; bundleVersion: number }>(
          `/api/models/${encodeURIComponent(modelId)}/rooms/${encodeURIComponent(roomId)}/geometry`,
          {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...body, expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion) }),
          },
        );
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) void invalidateModel(qc, modelId, data);
      toast.success('Комната сохранена');
    },
    onError: (err) => toast.error('Не удалось изменить комнату', { description: String(err) }),
  });
}

export function usePatchWallFaceMaterial(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { wallEntityId: string; materialId: string }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await requestJson<{ ok: boolean; materialId: string; materialName: string; bundleVersion: number }>(
          `/api/models/${encodeURIComponent(modelId)}/walls/${encodeURIComponent(payload.wallEntityId)}/material`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              materialId: payload.materialId,
              expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
            }),
          },
        );
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId);
      toast.success('Материал стены обновлён', { description: data.materialName });
    },
    onError: (err) => toast.error('Не удалось сменить материал стены', { description: String(err) }),
  });
}

export function useSwapModuleCatalog(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { instanceId: string; catalogId: string }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = requiredCommandBaseline(modelId, cached?.bundleVersion);
        const result = await requestJson<{ ok: boolean; oldCatalogId: string; newCatalogId: string; bundleVersion: number }>(
          `/api/models/${encodeURIComponent(modelId)}/modules/${encodeURIComponent(payload.instanceId)}/swap-catalog`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ catalogId: payload.catalogId, expectedBundleVersion: expected }),
          },
        );
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId) invalidateModel(qc, modelId);
      toast.success('Каталог заменён', { description: `${data.oldCatalogId} → ${data.newCatalogId}` });
    },
    onError: (err) => toast.error('Не удалось заменить каталог', { description: String(err) }),
  });
}

export function useValidateGraph(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (opts?: { persist?: boolean; dryRun?: boolean }) => {
      if (!modelId) throw new Error('No active model');
      const params: string[] = [];
      if (opts?.persist) params.push('persist=true');
      if (opts?.dryRun) params.push('dry_run=true');
      const qs = params.length ? '?' + params.join('&') : '';
      return postJson<{
        ok: boolean;
        modelId: string;
        report?: {
          schema: string;
          errors: number;
          warnings: number;
          pass: boolean;
          issues: { severity: string; concern: string; rule: string; message?: string; targetId?: string }[];
          byConcern: Record<string, number>;
        };
        dryRun?: boolean;
        persisted: boolean;
      }>(`/api/models/${encodeURIComponent(modelId)}/validate-graph${qs}`, {});
    },
    onSuccess: (data) => {
      if (modelId && data.persisted) invalidateModel(qc, modelId);
      if (data.dryRun) {
        const report = data.report;
        toast.info('Dry-run проверки выполнен', {
          description: `${report?.errors ?? '?'} ошибок, ${report?.warnings ?? '?'} предупр.`,
        });
        return;
      }
      const r = data.report;
      if (!r) return;
      const tone = r.pass ? 'success' : 'error';
      const msg = `${r.errors} ошибок · ${r.warnings} предупр.`;
      toast[tone](`Модель ${r.pass ? 'валидна' : 'не валидна'}`, { description: msg });
    },
    onError: (err) => toast.error('Validate не выполнен', { description: String(err) }),
  });
}

export function useAddCabinetToViewport(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, { mutationFn: async (body: Omit<Parameters<typeof api.addCabinetInstance>[1], 'expectedVersion'>, { invocationOwner }) => {
    if (!modelId) throw new Error('No active model');
    return enqueueModelCommand(modelId, async () => {
      const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
      const result = await api.addCabinetInstance(modelId, { ...body,
        expectedVersion: requiredCommandBaseline(modelId, cached?.bundleVersion) });
      rememberCommittedVersion(modelId, result);
      announceOwnModuleCommand(modelId, result);
      return result;
    }, invocationOwner);
  } }, true);
}

export function useAddModuleToViewport(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, { mutationFn: async (body: Omit<Parameters<typeof api.addModuleInstance>[1], 'expectedBundleVersion'>, { invocationOwner }) => {
    if (!modelId) throw new Error('No active model');
    return enqueueModelCommand(modelId, async () => {
      const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
      const result = await api.addModuleInstance(modelId, { ...body,
        expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion) });
      rememberCommittedVersion(modelId, result);
      announceOwnModuleCommand(modelId, result);
      return result;
    }, invocationOwner);
  } }, true);
}

export function useSaveModuleState(
  modelId: string | null | undefined,
  options: SaveModuleStateOptions = {},
) {
  const qc = useQueryClient();
  const silent = Boolean(options.silent);
  const shouldInvalidate = options.invalidate ?? true;
  return useOwnedMutation(modelId, {
    onMutate: (body) => {
      const owner = { modelId, modelEpoch: useViewerStore.getState().activeModelEpoch,
        submittedStateFingerprint: moduleStateFingerprint(normalizeEditableModuleState(body.transforms ?? {}, body.notes ?? {})) };
      window.dispatchEvent(new CustomEvent(MODULE_STATE_SAVE_STARTED_EVENT, { detail: owner }));
      return owner;
    },
    mutationFn: async (body: ModuleStateBody): Promise<SavedModuleStatePayload> => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cachedBundle = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const expected = queuedExpectedVersion(
          modelId,
          cachedBundle?.bundleVersion,
          body.expectedBundleVersion,
        );
        if (isSupersededOwnState(modelId, body, body.expectedBundleVersion ?? cachedBundle?.bundleVersion)) {
          // A synchronous command already committed this exact state and a
          // React effect or explicit UI action scheduled the old save
          // afterwards. Suppress only that exact own snapshot; a genuinely
          // dirty/remote edit still keeps its immutable base and receives the
          // normal 409.
          let current = committedModuleStates.get(modelId);
          if (!current || current.bundleVersion !== latestCommittedVersions.get(modelId)) {
            current = await api.moduleState(modelId);
            requireModuleStateAck(modelId, current);
          }
          // Return measured current state, never label the superseded body as
          // a saved snapshot. A readback is not a new same-window CAS edge.
          return { ...current, changed: false, stateReadbackOnly: true };
        }
        const { autosave: _autosave, ...wireBody } = body;
        const result = await postJson<ModuleStatePayload>(
          `/api/models/${encodeURIComponent(modelId)}/modules/state`,
          {
            ...wireBody,
            expectedBundleVersion: expected,
          },
        );
        if (result.discardedInvalidTransforms) {
          throw Object.assign(new Error('Перемещение отклонено: модель не проходит Graph-2'),
            { status: 422, code: 'MODULE_TRANSFORM_GRAPH_INVALID' });
        }
        requireModuleStateAck(modelId, result, expected);
        rememberCommittedVersion(modelId, result);
        rememberCommittedState(modelId, body, result);
        return result;
      });
    },
    onSuccess: (data, _variables, owner) => {
      const modelId = owner?.modelId;
      if (modelId && !data.stateReadbackOnly) rememberCommittedVersion(modelId, data);
      if (modelId) {
        window.dispatchEvent(new CustomEvent(MODULE_STATE_COMMITTED_EVENT, {
          detail: {
            modelId,
            modelEpoch: owner?.modelEpoch,
            submittedStateFingerprint: owner?.submittedStateFingerprint,
            bundleVersion: data.bundleVersion,
            bundleHash: data.bundleHash,
            state: data.state,
          },
        }));
      }
      if (modelId && shouldInvalidate) invalidateModuleRuntime(qc, modelId);
      if (modelId) {
        window.dispatchEvent(new CustomEvent(MODULE_STATE_SAVE_SETTLED_EVENT, {
          detail: { modelId, modelEpoch: owner?.modelEpoch, ok: true },
        }));
      }
      if (!silent) {
        if (data.changed) {
          toast.success('Состояние модулей сохранено в проект');
        } else {
          toast.message('Изменений для сохранения нет');
        }
      }
    },
    onError: (err, _variables, owner) => {
      const modelId = owner?.modelId;
      if (modelId) {
        window.dispatchEvent(new CustomEvent(MODULE_STATE_SAVE_SETTLED_EVENT, {
          detail: { modelId, modelEpoch: owner?.modelEpoch, ok: false, status: (err as { status?: number })?.status },
        }));
      }
      if (modelId && String(err).includes('bundle version conflict')) {
        void qc.invalidateQueries({ queryKey: queryKeys.bundle(modelId) });
      }
      if (!silent) {
        toast.error('Не удалось сохранить модульные правки', { description: String(err) });
      }
    },
  });
}

type FillMutationContext = {
  previousFill?: ModuleFillState;
  patched?: boolean;
};

export type ConstructorNicheClosureMigrationPreview = {
  mapping: { mappingSha256: string; productionReady: false; drawerBands: { runtimeRefusal?: string }[] };
  closure: { sourceId: string; sourceNicheIds: string[]; targetSlotIds: string[]; sourceIntent: Record<string, unknown> };
  bundleVersion: number;
};

export function useImportConstructorNicheClosure(modelId: string | null | undefined, moduleId: string | null | undefined) {
  const qc = useQueryClient();
  const commandBody = (sourceContract: Record<string, unknown>, sourceClosureId: string) => {
    if (!modelId || !moduleId) throw new Error('No active module');
    const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
    return { sourceContract, sourceClosureId, createSnapshot: true, expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion) };
  };
  const preview = useOwnedMutation(modelId, {
    mutationFn: async ({ sourceContract, sourceClosureId }: { sourceContract: Record<string, unknown>; sourceClosureId: string }) => {
      if (!modelId || !moduleId) throw new Error('No active module');
      return postJson<ConstructorNicheClosureMigrationPreview>(
        `/api/models/${encodeURIComponent(modelId)}/modules/${encodeURIComponent(moduleId)}/fill/import-constructor-closure/preview`,
        commandBody(sourceContract, sourceClosureId),
      );
    },
    onError: (err) => toast.error('PRO100-контракт не принят', { description: String(err) }),
  });
  const apply = useOwnedMutation(modelId, {
    mutationFn: async ({ sourceContract, sourceClosureId }: { sourceContract: Record<string, unknown>; sourceClosureId: string }) => {
      if (!modelId || !moduleId) throw new Error('No active module');
      return enqueueModelCommand(modelId, async () => {
        const result = await postJson<{ ok: true; status: 'APPLIED' | 'ALREADY_APPLIED'; changed: boolean; bundleVersion: number; bundleHash?: string }>(
          `/api/models/${encodeURIComponent(modelId)}/modules/${encodeURIComponent(moduleId)}/fill/import-constructor-closure`,
          commandBody(sourceContract, sourceClosureId),
        );
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId && moduleId) invalidateModuleFill(qc, modelId, moduleId, data);
      toast.success(data.status === 'ALREADY_APPLIED' ? 'PRO100-закрытие уже перенесено' : 'PRO100-закрытие перенесено');
    },
    onError: (err) => toast.error('PRO100-закрытие не перенесено', { description: String(err) }),
  });
  return { preview, apply };
}

export function useApplyModuleFill(modelId: string | null | undefined, moduleId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (body: ModuleFillOperationPayload) => {
      if (!modelId || !moduleId) throw new Error('No active module');
      return enqueueModelCommand(modelId, async () => {
        const cachedBundle = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.applyModuleFill(modelId, moduleId, {
          ...body,
          expectedBundleVersion: requiredCommandBaseline(modelId, cachedBundle?.bundleVersion),
        });
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onMutate: async (body): Promise<FillMutationContext> => {
      const authGeneration = authenticatedCacheGeneration();
      if (!modelId || !moduleId) return {};
      const key = queryKeys.moduleFill(modelId, moduleId);
      // Stop any in-flight plan fetch from clobbering the optimistic write.
      await qc.cancelQueries({ queryKey: key });
      requireAuthenticatedGeneration(authGeneration);
      const previousFill = qc.getQueryData<ModuleFillState>(key);
      let patched = false;
      if (previousFill && isItemScopedFillOp(body.operation)) {
        const next = optimisticFillPatch(previousFill, body);
        if (next !== previousFill) {
          qc.setQueryData(key, next);
          patched = true;
        }
      }
      return { previousFill, patched };
    },
    onSuccess: (data, variables) => {
      if (modelId) rememberCommittedVersion(modelId, data);
      if (modelId && moduleId) {
        invalidateModuleFill(qc, modelId, moduleId, data, { scoped: isItemScopedFillOp(variables.operation) });
      }
      const n = data?.conflicts?.length ?? 0;
      if (n > 0) toast.warning(`Наполнение обновлено, но есть ${n} пересечени${n === 1 ? 'е' : 'й'}`);
      else toast.success('Наполнение модуля обновлено');
    },
    onError: (err, _variables, context: FillMutationContext | undefined) => {
      if (context?.patched && context.previousFill && modelId && moduleId) {
        qc.setQueryData(queryKeys.moduleFill(modelId, moduleId), context.previousFill);
      }
      toast.error('Не удалось изменить наполнение', { description: String(err) });
    },
  });
}

export function useApplyModuleAtelier(modelId: string | null | undefined, moduleId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (body: {
      intent: ModuleAtelierIntent;
      resolution: ModuleAtelierResolution;
      expectedBundleVersion: number;
    }) => {
      if (!modelId || !moduleId) throw new Error('No active module');
      return enqueueModelCommand(modelId, async () => {
        const result = await api.applyModuleAtelier(modelId, moduleId, {
          ...body,
          createSnapshot: true,
        });
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (result) => {
      if (!modelId || !moduleId) return;
      qc.setQueryData(queryKeys.moduleAtelier(modelId, moduleId), result);
      void invalidateModel(qc, modelId, result);
    },
  });
}

export function useApplyFillScheme(modelId: string | null | undefined, moduleId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (body: {
      schemeId: string;
      slotId?: string;
      mount?: 'inset' | 'overlay' | 'half_overlay';
      facadeProfile?: string;
      facadeMaterialId?: number;
      facadeMaterialSourceReleaseSha?: string;
      facadeMaterialRowSha256?: string;
      materialProfile?: string;
      openMode?: 'hinged' | 'push' | 'lift' | 'drop_down';
      hingeType?: string;
      aventosType?: string;
      sprossen?: number;
      slideSystem?: 'tandembox' | 'legrabox' | 'movento' | 'quadro' | 'roller';
      softClose?: boolean;
      servoDrive?: boolean;
      pushToOpen?: boolean;
      railing?: boolean;
      thicknessMm?: number;
      moduleIds?: string[];
      params?: Record<string, number>;
      createSnapshot?: boolean;
    }) => {
      if (!modelId || !moduleId) throw new Error('No active module');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.applyFillScheme(
          modelId,
          moduleId,
          { ...body, expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion) },
        );
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onSuccess: (data) => {
      if (modelId && moduleId) invalidateModuleFill(qc, modelId, moduleId, data);
      // Multi-module apply also touches sibling modules' fill — refetch the
      // whole bundle so their plans refresh too. (Graph/history invalidation is
      // handled centrally by invalidateModuleFill above.)
      if (modelId) {
        void qc.invalidateQueries({ queryKey: queryKeys.bundle(modelId) });
      }
      const replacedFronts = data?.replacedFronts ?? 0;
      /* ВОЛНА W «ЖИВОЙ 3D ПОВСЮДУ»: фасадная схема встаёт КАСКАДОМ панелей
       * (стагер 40 мс), схема без фасадов (полки/ящики внутри) — мягким вдохом
       * модуля. Количество панелей — из ответа сервера, клиент форму не выдумывает. */
      if (replacedFronts > 0) {
        announcePlacementEffect({ kind: 'front-cascade', moduleId: moduleId!, panels: replacedFronts }, modelId);
      } else {
        announcePlacementEffect({ kind: 'module-settle', moduleId: moduleId! }, modelId);
      }
      const appliedList = (data as { appliedModules?: string[]; totalConflicts?: number } | undefined)?.appliedModules;
      const applied = Array.isArray(appliedList) ? appliedList.length : 1;
      const total = (data as { totalConflicts?: number } | undefined)?.totalConflicts;
      const n = applied > 1 && typeof total === 'number' ? total : data?.conflicts?.length ?? 0;
      const scope = applied > 1 ? ` к ${applied} модулям` : '';
      if (n > 0) {
        toast.warning(`Схема применена${scope}, но есть ${n} пересечени${n === 1 ? 'е' : 'й'}`);
      } else {
        toast.success(replacedFronts > 0 ? `Схема применена${scope} (заменено фасадов: ${replacedFronts})` : `Схема наполнения применена${scope}`);
      }
    },
    onError: (err) => toast.error('Не удалось применить схему', { description: String(err) }),
  });
}

type DynamicFillSchemeBody = Omit<Parameters<typeof api.applyFillScheme>[2], 'expectedBundleVersion'>;

/** Viewport DnD variant: the raycast selects the module at command time. */
export type ViewerFillPreset = {
  id: string;
  name: string;
  baseSchemeId: string;
  params: Record<string, number>;
  createdAt: string;
};

/** A9: пресеты наполнения — сохранить текущие параметры схемы и переиспользовать
 *  их позже. Сервер держит пресеты в bundle.viewerHints.customFillPresets
 *  (до 50) и инвалидирует bundle; фронт только зовёт и перезагружает. */
export function useSaveFillPreset(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (body: { name: string; baseSchemeId: string; params?: Record<string, number> }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => api.saveFillPreset(modelId, body));
    },
    onSuccess: () => {
      if (modelId) void qc.invalidateQueries({ queryKey: queryKeys.bundle(modelId) });
    },
  });
}

export function useDeleteFillPreset(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (presetId: string) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => api.deleteFillPreset(modelId, presetId));
    },
    onSuccess: () => {
      if (modelId) void qc.invalidateQueries({ queryKey: queryKeys.bundle(modelId) });
    },
  });
}

export function useApplyFillSchemeToModule(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { moduleId: string; body: DynamicFillSchemeBody }, { invocationOwner }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.applyFillScheme(modelId, payload.moduleId, {
          ...payload.body,
          expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
        });
        rememberCommittedVersion(modelId, result);
        return result;
      }, invocationOwner);
    },
    onSuccess: (data, payload) => {
      if (modelId) invalidateModuleFill(qc, modelId, payload.moduleId, data);
      /* ВОЛНА W: viewport-дроп схемы — тот же каскад панелей, что и кнопка. */
      const replacedFronts = data?.replacedFronts ?? 0;
      if (replacedFronts > 0) {
        announcePlacementEffect({ kind: 'front-cascade', moduleId: payload.moduleId, panels: replacedFronts }, modelId);
      } else {
        announcePlacementEffect({ kind: 'module-settle', moduleId: payload.moduleId }, modelId);
      }
    },
  }, true);
}

/** Viewport DnD variant for a single fill operation and dynamic module target. */
export function useApplyModuleFillToModule(modelId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (payload: { moduleId: string; body: ModuleFillOperationPayload }, { invocationOwner }) => {
      if (!modelId) throw new Error('No active model');
      return enqueueModelCommand(modelId, async () => {
        const cached = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.applyModuleFill(modelId, payload.moduleId, {
          ...payload.body,
          expectedBundleVersion: requiredCommandBaseline(modelId, cached?.bundleVersion),
        });
        rememberCommittedVersion(modelId, result);
        return result;
      }, invocationOwner);
    },
    onSuccess: (data, payload) => {
      if (modelId) invalidateModuleFill(qc, modelId, payload.moduleId, data);
      /* ВОЛНА W: предмет вырастает от плоскости ниши (~180 мс) + пульс её границ.
       * Только для операций «в нишу ПРИБАВИЛОСЬ предмет»: правки/удаления/
       * ресайз границ молчат — там нечему приземляться. */
      const slot = fillItemAppearSlotOf(payload.body);
      if (slot !== null) {
        announcePlacementEffect({ kind: 'fill-item', moduleId: payload.moduleId, slotId: slot }, modelId);
      }
    },
  }, true);
}

/** Ниша, у которой предмет появится, — или null, если операция не «прибытие».
 *  Вынесено отдельно, чтобы пин проверял классификацию без мутации. */
export function fillItemAppearSlotOf(body: ModuleFillOperationPayload): string | null | undefined {
  const arrival: Record<string, true> = {
    shelf: true, shelves: true, set_shelves: true, partition: true, set_partitions: true,
    facades: true, drawers: true, sliding: true, rod: true, pantograph: true, basket: true,
    dish_dryer: true, under_sink_bins: true, waste_sorter: true, cutlery_tray: true,
    internal_drawer: true, led: true, under_cabinet_led: true, cargo: true, corner_mech: true,
    appliance: true, duplicate_item: true,
  };
  if (body.operation === 'move_item') return body.targetSlotId ?? body.slotId ?? null;
  if (!arrival[body.operation]) return null;
  return body.slotId ?? undefined;
}

export function useRemoveModuleFillItem(modelId: string | null | undefined, moduleId: string | null | undefined) {
  const qc = useQueryClient();
  return useOwnedMutation(modelId, {
    mutationFn: async (itemId: string) => {
      if (!modelId || !moduleId) throw new Error('No active module');
      return enqueueModelCommand(modelId, async () => {
        const cachedBundle = qc.getQueryData<UniversalBundle>(queryKeys.bundle(modelId));
        const result = await api.removeModuleFillItem(
          modelId,
          moduleId,
          itemId,
          true,
          requiredCommandBaseline(modelId, cachedBundle?.bundleVersion),
        );
        rememberCommittedVersion(modelId, result);
        return result;
      });
    },
    onMutate: async (itemId): Promise<FillMutationContext> => {
      const authGeneration = authenticatedCacheGeneration();
      if (!modelId || !moduleId) return {};
      const key = queryKeys.moduleFill(modelId, moduleId);
      await qc.cancelQueries({ queryKey: key });
      requireAuthenticatedGeneration(authGeneration);
      const previousFill = qc.getQueryData<ModuleFillState>(key);
      let patched = false;
      if (previousFill) {
        const next = optimisticFillPatch(previousFill, { operation: 'delete_item', itemId });
        if (next !== previousFill) {
          qc.setQueryData(key, next);
          patched = true;
        }
      }
      return { previousFill, patched };
    },
    onSuccess: (data) => {
      if (modelId) rememberCommittedVersion(modelId, data);
      if (modelId && moduleId) invalidateModuleFill(qc, modelId, moduleId, data, { scoped: true });
      toast.success('Элемент наполнения удалён');
    },
    onError: (err, _itemId, context: FillMutationContext | undefined) => {
      if (context?.patched && context.previousFill && modelId && moduleId) {
        qc.setQueryData(queryKeys.moduleFill(modelId, moduleId), context.previousFill);
      }
      toast.error('Не удалось удалить элемент', { description: String(err) });
    },
  });
}
